import { useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { auth } from "@/lib/auth"
import { Badge, Button, Card, CardBody, Input, Sheet, Spinner, Textarea, Toast } from "@/components/ui"
import { cn, timeAgo } from "@/lib/utils"
import type { AutonomyLevel, LocalAgentStatusOut, ProjectOut } from "@/lib/types"
import { FolderGitIcon, PlusIcon, LaptopIcon, ShieldCheckIcon } from "lucide-react"

export const Route = createFileRoute("/_app/projects")({
  component: ProjectsPage,
})

const LEVELS: { value: AutonomyLevel; label: string; blurb: string }[] = [
  { value: "observe",      label: "Observe",      blurb: "Read-only. Lucy can look at files, never run or write." },
  { value: "propose",      label: "Propose",      blurb: "Lucy drafts commands and edits; every one waits for your OK." },
  { value: "execute_safe", label: "Execute safe", blurb: "Low-risk actions run on their own (build, test, read, small edits); risky ones ask first." },
  { value: "execute",      label: "Execute",      blurb: "Lucy runs the job end-to-end inside the folder — commands, files, Claude CLI." },
]

function ProjectsPage() {
  const me = auth.me()
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [toast, setToast] = useState<string | null>(null)

  const projects = useQuery({
    queryKey: ["projects", me?.id],
    queryFn: () => api<ProjectOut[]>("/api/projects", { query: { user_id: me?.id } }),
    enabled: !!me?.id,
  })
  const agent = useQuery({
    queryKey: ["local-agent", me?.id],
    queryFn: () => api<LocalAgentStatusOut>("/api/dashboard/connectors/local-agent/status", { query: { user_id: me?.id } }),
    enabled: !!me?.id,
    refetchInterval: 15_000,
  })

  const patch = useMutation({
    mutationFn: (args: { id: string; body: Record<string, unknown> }) =>
      api<ProjectOut>(`/api/projects/${args.id}`, { method: "PATCH", body: args.body, query: { user_id: me?.id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["projects"] }); setToast("Saved") },
    onError: (e) => setToast((e as Error).message),
  })
  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<ProjectOut>("/api/projects", { method: "POST", body, query: { user_id: me?.id } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["projects"] }); setCreating(false) },
    onError: (e) => setToast((e as Error).message),
  })

  const online = !!agent.data?.registered && !!agent.data.last_seen_at && Date.now() - new Date(agent.data.last_seen_at).getTime() < 120_000
  const caps = agent.data?.capabilities ?? []
  const canExec = caps.includes("exec")

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Projects</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Folders on your Mac that Lucy may work in, and how much she may do there on her own.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}><PlusIcon className="h-4 w-4" /> New project</Button>
      </div>

      {/* Mac agent status */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-3 text-sm">
            <span className={cn("h-2.5 w-2.5 rounded-full", online ? "bg-emerald-500" : "bg-red-500")} />
            <LaptopIcon className="h-4 w-4 text-muted-foreground" />
            {online ? (
              <span>
                Mac app connected{agent.data?.last_seen_at ? ` · seen ${timeAgo(agent.data.last_seen_at)}` : ""}.{" "}
                {canExec
                  ? <>Local execution <b>on</b>{caps.includes("claude_cli") ? " (incl. Claude CLI)" : ""}{caps.includes("fs") ? "" : ""}.</>
                  : <span className="text-amber-600">Local execution is <b>off</b> — enable it in the Mac app: Settings → Local execution, and add the project folders there.</span>}
              </span>
            ) : (
              <span className="text-muted-foreground">Mac app not connected. Open Lucy on your Mac and sign in — it connects automatically.</span>
            )}
          </div>
        </CardBody>
      </Card>

      {projects.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Spinner /> Loading…</div>
      ) : (projects.data ?? []).length === 0 ? (
        <Card><CardBody>
          <div className="py-10 text-center text-sm text-muted-foreground">
            No projects yet. Create one, point it at a folder on your Mac, and pick how autonomous Lucy may be there.
          </div>
        </CardBody></Card>
      ) : (
        <div className="space-y-3">
          {(projects.data ?? []).map((p) => (
            <ProjectCard key={p.id} p={p} onPatch={(body) => patch.mutate({ id: p.id, body })} />
          ))}
        </div>
      )}

      <Sheet open={creating} onClose={() => setCreating(false)} title="New project">
        <form className="space-y-3" onSubmit={(e) => {
          e.preventDefault()
          const fd = new FormData(e.currentTarget)
          const name = String(fd.get("name") || "").trim()
          if (!name) return
          create.mutate({
            name,
            local_path: String(fd.get("local_path") || "").trim() || null,
            description: String(fd.get("description") || "").trim() || null,
          })
        }}>
          <label className="block text-xs text-muted-foreground">Name<Input name="name" placeholder="e.g. React calculator" autoFocus className="mt-1" /></label>
          <label className="block text-xs text-muted-foreground">Folder on your Mac<Input name="local_path" placeholder="/Users/you/Projects/calculator" className="mt-1 font-mono" /></label>
          <label className="block text-xs text-muted-foreground">What it is (optional)<Textarea name="description" rows={2} className="mt-1" /></label>
          <p className="text-xs text-muted-foreground">Starts at <b>Observe</b>. Raise the dial on the card once the folder is also listed in the Mac app.</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
            <Button type="submit" size="sm" disabled={create.isPending}>Create</Button>
          </div>
        </form>
      </Sheet>

      {toast && <Toast variant={toast === "Saved" ? "ok" : "destructive"}>{toast}</Toast>}
    </div>
  )
}

function ProjectCard({ p, onPatch }: { p: ProjectOut; onPatch: (body: Record<string, unknown>) => void }) {
  const [path, setPath] = useState(p.local_path ?? "")
  const [notes, setNotes] = useState(p.context_notes ?? "")
  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FolderGitIcon className="h-4 w-4 text-muted-foreground" />
              <h2 className="font-medium truncate">{p.name}</h2>
              <Badge>{p.status}</Badge>
              {p.last_activity_at && <span className="text-xs text-muted-foreground">active {timeAgo(p.last_activity_at)}</span>}
            </div>
            {p.description && <p className="text-xs text-muted-foreground mt-1">{p.description}</p>}
          </div>
          <select
            className="h-8 rounded-md border bg-background px-2 text-xs"
            value={p.status}
            onChange={(e) => onPatch({ status: e.target.value })}
          >
            <option value="active">active</option>
            <option value="paused">paused</option>
            <option value="archived">archived</option>
          </select>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="block text-xs text-muted-foreground">Folder on your Mac</label>
            <div className="flex gap-2">
              <Input value={path} onChange={(e) => setPath(e.target.value)} placeholder="/Users/you/Projects/…" className="font-mono text-xs" />
              <Button size="sm" variant="outline" disabled={path === (p.local_path ?? "")}
                onClick={() => onPatch(path.trim() ? { local_path: path.trim() } : { clear_local_path: true })}>Save</Button>
            </div>
            <label className="block text-xs text-muted-foreground mt-2">Notes for Lucy (stack, conventions, what “done” means)</label>
            <div className="flex gap-2">
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="text-xs" />
              <Button size="sm" variant="outline" disabled={notes === (p.context_notes ?? "")}
                onClick={() => onPatch(notes.trim() ? { context_notes: notes.trim() } : { clear_context_notes: true })}>Save</Button>
            </div>
          </div>

          <div>
            <label className="block text-xs text-muted-foreground mb-2 flex items-center gap-1"><ShieldCheckIcon className="h-3.5 w-3.5" /> Autonomy</label>
            <div className="space-y-1.5">
              {LEVELS.map((l) => (
                <button key={l.value} onClick={() => onPatch({ autonomy_level: l.value })}
                  className={cn("w-full text-left rounded-md border px-3 py-2 transition",
                    p.autonomy_level === l.value ? "border-primary bg-primary/10" : "hover:bg-accent")}>
                  <div className="text-sm font-medium">{l.label}</div>
                  <div className="text-xs text-muted-foreground">{l.blurb}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
